import { DroidmapFormalEnvironmentName } from '@droidmap/shared-env-context/runtime'

// This is a copy of the PutEventsRequestEntry type from the
// @aws-sdk/client-eventbridge package.
// We use this type in our service contracts to define events
// that can be sent or arrive from a Service. We don't want to
// drag the whole @aws-sdk/client-eventbridge package into our
// frontend bundle though we copy the type here. If the source
// type changes in the future we will need to update this type.
export type DroidMapPutEventsRequestEntry = {
  Time?: Date
  Source?: string
  Resources?: string[]
  DetailType?: string
  Detail?: string
  EventBusName?: string
  TraceHeader?: string
}

/**
 * This type is used to represent the metadata that is sent in an event.
 * It includes information about the source, user, connection, trace, and
 * group that the event pertains to. This information can be used to send
 * updates back out to the originator that initiated the event.
 */
export type DroidMapMetadata = {
  userId: string
  orgId: string
  projectId?: string

  /**
   * Token scopes - only present when request is authenticated via token authorizer.
   * Used to restrict what operations a shared token can perform.
   * When present, permissions checks should validate the required permission
   * is allowed by the token's scopes.
   */
  tokenScopes?: string[]

  /**
   * The staff member really behind this request or event, when `userId` is
   * not the person acting: a staff member viewing the app as the customer
   * (droidmap#2651). Absent for customers' own requests. Rows and events
   * written on such a request record it, so a change is never attributed to
   * the customer alone.
   */
  actingStaffId?: string

  /**
   * The kind of support session `actingStaffId` acts in: `observe` (as the
   * customer, read-only) or `assist`. Set with `actingStaffId`, never alone.
   */
  actingStaffKind?: 'observe' | 'assist'

  // xrayTraceId is used to propagate trace context for aws xray
  xrayTraceId?: string
  // trace parent and state are used to propagate trace context for
  // open telemetry
  traceParent?: string
  traceState?: string
  connectionId?: string
  sourceChain?: string[]

  /**
   * DroidMap environment that emitted the event (e.g. `dev`,
   * `prod-ap-southeast-2`). Optional on the base metadata because most
   * intra-account event consumers don't need it — but emitters whose
   * events are forwarded cross-account into the shared ANALYTICS account
   * must stamp it so the analytics-side handler can discriminate
   * environments off a single shared bus. Mirrors the required
   * `environment` field on `DroidMapUsageMetadata`, where it serves the
   * same purpose for the usage-event pipeline.
   */
  environment?: DroidmapFormalEnvironmentName
}

/**
 * This type is used to represent the data that is sent in an event.
 * It is a wrapper around the data that is sent in the event and
 * includes metadata about the event.
 */
export type DroidMapEventDetail<T> = T & {
  metadata: DroidMapMetadata
}

/**
 * This type is used to represent the data that is sent in an update event.
 * It includes the previous and updated versions of the data and the
 * properties that have changed.
 */
export type DroidMapUpdateEventDetail<T> = {
  previous: T
  updated: T
  changed: Partial<T>
  metadata: DroidMapMetadata
  storageAndAssetCountDelta?: StorageAndAssetCountDelta
}

/**
 * This type is used to represent the delta (change) in storage summary and asset count
 */
export type StorageAndAssetCountDelta = {
  assetCountDelta: number
  storageSummaryDelta: {
    standard: number
    standardIA: number
    oneZoneIA: number
    glacierIR: number
    glacierFlex: number
    glacierDA: number
  }
}

/**
 *
 * The event type for EventBridge (PutEventsRequestEntry) has a payload
 * field called Detail that is a string. Where ever we use this type
 * though we are nearly always going to want to pass a JSON object with
 * specific type information. This type is a wrapper around the
 * PutEventsRequestEntry type that allows us to pass a custom type
 * to the DroidMapEventDetail data field.
 * We also omit the EventBusName field as we will always want to use
 * the droidmap event bus for the current environment and we don't want to
 * work out this event bus name in every place we send an event. This
 * value will populated in the DroidMapEventEmitter class.
 */
export type DroidMapEvent<T, DT extends string> = Omit<
  DroidMapPutEventsRequestEntry,
  'Detail' | 'EventBusName'
> & {
  Detail: DroidMapEventDetail<T>
  DetailType: DT
}

/**
 * This type is used to represent the data that is sent in an update event.
 * It includes the previous and updated versions of the data and the
 * properties that have changed.
 */
export type DroidMapUpdateEvent<T, DT extends string> = Omit<
  DroidMapPutEventsRequestEntry,
  'Detail' | 'EventBusName'
> & {
  Detail: DroidMapUpdateEventDetail<T>
  DetailType: DT
}

/**
 * This class is used to convert between the DroidMapEvent type and the
 * PutEventsRequestEntry type. It is used to convert the Detail field
 * from a string to a generic type and vice versa.
 */
export class DroidMapEventBridgeAdaptor {
  static create<T, DT extends string>(
    event: DroidMapEvent<T, DT>,
    eventBusName: string,
  ): DroidMapPutEventsRequestEntry {
    return {
      ...event,
      EventBusName: eventBusName,
      Detail: JSON.stringify(event.Detail),
    }
  }

  static parse<T, DT extends string>(
    event: DroidMapPutEventsRequestEntry,
  ): DroidMapEvent<T, DT> {
    return {
      ...event,
      Detail: JSON.parse(event.Detail as string) as DroidMapEventDetail<T>,
      DetailType: event.DetailType as DT,
    }
  }

  static parseUpdate<T, DT extends string>(
    event: DroidMapPutEventsRequestEntry,
  ): DroidMapUpdateEvent<T, DT> {
    return {
      ...event,
      Detail: JSON.parse(
        event.Detail as string,
      ) as DroidMapUpdateEventDetail<T>,
      DetailType: event.DetailType as DT,
    }
  }

  static getDetail<T>(
    event: DroidMapPutEventsRequestEntry,
  ): DroidMapEventDetail<T> {
    return JSON.parse(event.Detail as string) as DroidMapEventDetail<T>
  }

  static getUpdateDetail<T>(
    event: DroidMapPutEventsRequestEntry,
  ): DroidMapUpdateEventDetail<T> {
    return JSON.parse(event.Detail as string) as DroidMapUpdateEventDetail<T>
  }
}

/**
 * This class is used to create and parse events for a specific event
 * type. It is used to create events that can be sent to EventBridge
 * and to parse events that are received from EventBridge.
 */
export class BaseEventBuilder<T, DT extends string> {
  public readonly source: string
  public readonly detailType: DT

  constructor(source: string, detailType: DT) {
    this.source = source
    this.detailType = detailType
  }

  private buildDetail(
    detail: T & {
      metadata: DroidMapMetadata
    },
  ): DroidMapEventDetail<T> {
    return {
      ...detail,
      metadata: {
        ...detail.metadata,
        sourceChain: detail.metadata?.sourceChain
          ? [...detail.metadata.sourceChain, this.source]
          : [this.source],
      },
    }
  }

  private buildUpdateDetail(
    previous: T,
    updated: T,
    changed: Partial<T>,
    metadata: DroidMapMetadata,
    storageAndAssetCountDelta?: StorageAndAssetCountDelta,
  ): DroidMapUpdateEventDetail<T> {
    return {
      previous,
      updated,
      changed,
      storageAndAssetCountDelta,
      metadata,
    }
  }

  public create(
    detail: T & {
      metadata: DroidMapMetadata
    },
  ): DroidMapEvent<T, DT> {
    const builtDetail = this.buildDetail(detail)
    return {
      Source: this.source,
      DetailType: this.detailType,
      Detail: builtDetail,
    }
  }

  public createUpdate(
    previous: T,
    updated: T,
    changed: Partial<T>,
    metadata: DroidMapMetadata,
    storageAndAssetCountDelta?: StorageAndAssetCountDelta,
  ): DroidMapUpdateEvent<T, DT> {
    const builtDetail = this.buildUpdateDetail(
      previous,
      updated,
      changed,
      metadata,
      storageAndAssetCountDelta,
    )
    return {
      Source: this.source,
      DetailType: this.detailType,
      Detail: builtDetail,
    }
  }

  public parseDetail(
    event: DroidMapPutEventsRequestEntry,
  ): DroidMapEventDetail<T> {
    return DroidMapEventBridgeAdaptor.getDetail<T>(event)
  }

  public parseEvent(
    event: DroidMapPutEventsRequestEntry,
  ): DroidMapEvent<T, DT> {
    return DroidMapEventBridgeAdaptor.parse<T, DT>(event)
  }

  public parseUpdateEvent(
    event: DroidMapPutEventsRequestEntry,
  ): DroidMapUpdateEvent<T, DT> {
    return DroidMapEventBridgeAdaptor.parseUpdate<T, DT>(event)
  }
}
